import { Button, ThemeSelect, Surface } from '../../uikit';
import React, {useRef, useState} from 'react';
import {ArrowRight, ArrowUpRight, Check, FileText, Stack, TextAlignLeft, Presentation} from '@phosphor-icons/react';
import LoginPage from './LoginPage';
import AuthModal from './AuthModal';
import {recoveredEmail} from '../../state/authJourney';

type Props = {onLogin: React.ComponentProps<typeof LoginPage>['onLogin']; autoOpen?: boolean};
function recoveryRequested() {
  try { return sessionStorage.getItem('pp_forgot_requested') === '1'; } catch { return false; }
}

export default function LandingPage({onLogin, autoOpen = false}: Props) {
  const [open, setOpen] = useState(() => autoOpen || !!recoveredEmail() || recoveryRequested());
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const returnFocus = useRef<HTMLElement | null>(null);
  const signIn = useRef<HTMLButtonElement>(null);
  function showLogin(event: React.MouseEvent<HTMLButtonElement>) {
    returnFocus.current = event.currentTarget;
    setOpen(true);
  }
  function closeLogin() { if (!busy) setOpen(false); }

  return <div className="pp-landing">
    <header className="pp-landing-header">
      <a className="pp-landing-brand" href="/" aria-label="PitchPilot home"><span className="pp-landing-mark" aria-hidden="true"><i/><i/><i/></span>PitchPilot</a>
      <nav className="pp-landing-nav" aria-label="Main navigation">
        <ThemeSelect/><a href="#how-it-works">How it works</a>
        <Button className="pp-landing-sign" ref={signIn} onClick={showLogin} aria-haspopup="dialog">Sign in <ArrowUpRight size={16}/></Button>
      </nav>
    </header>
    <main>
      <section className="pp-landing-hero" aria-labelledby="landing-title">
        <div>
          <p className="pp-landing-eyebrow"><span/>A little thought. A bigger story.</p>
          <h1 id="landing-title">Good ideas deserve<br/><em>a great telling.</em></h1>
          <p className="pp-landing-lead">Turn your notes, documents, and big ideas into a presentation worth sharing.</p>
          <Button variant="primary" className="pp-landing-primary" onClick={showLogin} aria-haspopup="dialog">Start your story <ArrowRight size={22}/></Button>
          <p className="pp-landing-note">Bring the idea. We’ll help you shape it.</p>
        </div>
        <div className="pp-landing-showcase" aria-label="Example presentation">
          <Surface className="pp-landing-preview-frame">
            <div className="pp-landing-preview-bar"><span><Presentation size={16} aria-hidden="true"/> Your next presentation</span><span className="pp-landing-preview-label">Example presentation</span></div>
            <div className="pp-landing-slide">
              <div className="pp-landing-slide-top"><span>A NEW PERSPECTIVE</span><span>01 / 08</span></div>
              <h2>Small beginnings.<br/><em>Bigger possibilities.</em></h2>
              <div className="pp-landing-sculpture" aria-hidden="true"/>
              <div className="pp-landing-slide-foot"><span>YOUR NEXT CHAPTER</span><ArrowUpRight size={12} aria-hidden="true"/></div>
            </div>
            <div className="pp-landing-thumbnails" aria-hidden="true">
              <div className="pp-landing-thumbnail pp-landing-thumbnail-cover"><span>01</span><i/><i/></div>
              <div className="pp-landing-thumbnail pp-landing-thumbnail-story"><span>02</span><i/><i/><i/></div>
              <div className="pp-landing-thumbnail pp-landing-thumbnail-chart"><span>03</span><i/><i/><i/></div>
              <div className="pp-landing-thumbnail pp-landing-thumbnail-end"><span>04</span><i/></div>
            </div>
          </Surface>
          <div className="pp-landing-input-note"><span className="pp-landing-file"><FileText size={22} aria-hidden="true"/></span><div>The starting point<small>Notes, documents &amp; a little ambition</small></div><span className="pp-landing-check"><Check size={14} aria-hidden="true"/></span></div>
        </div>
      </section>
      <section className="pp-landing-process" id="how-it-works" aria-label="How it works">
        <article><div className="pp-landing-step-top"><span className="pp-landing-step-icon"><Stack size={23} aria-hidden="true"/></span><span>01</span></div><h3>Bring your ideas</h3><p>A brief, a document, a few rough notes. Start with what you have.</p></article>
        <article><div className="pp-landing-step-top"><span className="pp-landing-step-icon"><TextAlignLeft size={23} aria-hidden="true"/></span><span>02</span></div><h3>Shape the story</h3><p>Work with your agent to find the thread and make every point count.</p></article>
        <article><div className="pp-landing-step-top"><span className="pp-landing-step-icon"><Presentation size={23} aria-hidden="true"/></span><span>03</span></div><h3>Make it presentable</h3><p>Refine your slides and speaker notes. Give your ideas the room.</p></article>
      </section>
    </main>
    <footer className="pp-landing-footer"><span>PitchPilot</span><span>From a first thought to a great telling.</span></footer>
    {open && <AuthModal busy={busy} onClose={closeLogin} returnFocusRef={returnFocus} fallbackFocusRef={signIn}>
      <LoginPage embedded headingId="landing-login-heading" initialEmail={email} onEmailChange={setEmail} onBusyChange={setBusy} onLogin={onLogin}/>
    </AuthModal>}
  </div>;
}
